/** Presentation-only opening card. Never narration, a role, or an always-on notice. */
export type SourceBrandIntro = {
  version: 1;
  title: string;
  subtitle?: string;
  bg?: string;
  fg?: string;
  width: number;
  height: number;
  /** Local source window; left trim/split subtract offset / native FPS from both ends. */
  clock: { startInSeconds: number; endInSeconds: number };
  sourceFps: number;
  provenance: {
    kind: 'native' | 'story';
    originalStartSec: number;
    originalEndSec: number;
    sourceId?: string;
  };
  /** Conservative timing receipt. Revalidate against live state before ripple removal. */
  leadingInterval?: { frames: number; layout: string; card: string };
};
export type BrandIntroTextPatch = { title?: string; subtitle?: string };
export const SOURCE_BRAND_INTRO_SECONDS = 3;
export const SOURCE_BRAND_INTRO_BG = '#111827';
export const SOURCE_BRAND_INTRO_FG = '#ffffff';
export const SOURCE_BRAND_INTRO_SUBTITLE = '본 영상은 AI로 제작한 교육용 콘텐츠입니다';
export const SOURCE_BRAND_INTRO_IMAGE = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVR4nGNgAAIAAAUAAXpeqz8AAAAASUVORK5CYII=';
const record = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const positive = (v: unknown): v is number => finite(v) && v > 0;
const text = (v: unknown): v is string => typeof v === 'string' && v.length <= 10000;
export function readSourceBrandIntro(v: unknown): SourceBrandIntro | undefined {
  if (v === undefined) return undefined;
  if (!record(v) || v.version !== 1 || !text(v.title) || !v.title.trim() ||
      ['subtitle', 'bg', 'fg'].some(k => v[k] !== undefined && !text(v[k])) ||
      !positive(v.width) || !positive(v.height) || v.width > 8192 || v.height > 8192 ||
      !positive(v.sourceFps) || !record(v.clock) || !finite(v.clock.startInSeconds) ||
      !finite(v.clock.endInSeconds) || v.clock.endInSeconds <= v.clock.startInSeconds ||
      !record(v.provenance) || !['native', 'story'].includes(String(v.provenance.kind)) ||
      !finite(v.provenance.originalStartSec) || v.provenance.originalStartSec < 0 ||
      !finite(v.provenance.originalEndSec) || v.provenance.originalEndSec <= v.provenance.originalStartSec ||
      (v.provenance.sourceId !== undefined && !text(v.provenance.sourceId))) throw Error('오프닝 카드 설정을 확인해 주세요.');
  if (v.leadingInterval !== undefined) {
    const r = v.leadingInterval;
    if (!record(r) || !Number.isSafeInteger(r.frames) || Number(r.frames) < 1 ||
        typeof r.layout !== 'string' || typeof r.card !== 'string') throw Error('오프닝 카드 시간 구간을 확인해 주세요.');
  }
  return v as SourceBrandIntro;
}
export function hasSourceBrandIntro(document: unknown): boolean {
  return record(document) && record(document.items) && Object.values(document.items).some(i => record(i) && i.sourceBrandIntro !== undefined);
}
export function validateSourceBrandIntroDocument(document: unknown): boolean {
  if (!record(document) || !record(document.items)) return false;
  try {
    return Object.values(document.items).every(i => {
      if (!record(i)) return false;
      const data = readSourceBrandIntro(i.sourceBrandIntro);
      if (!data) return true;
      if (i.type !== 'image' || i.sourceSlide !== undefined || i.sourceScene !== undefined || i.templateEffect !== undefined || i.ssulTemplateEffect !== undefined || i.sourceText !== undefined || i.cinematicMotion !== undefined || i.cinematicMasks !== undefined) return false;
      if (record(document.assets)) {
        const asset = document.assets[String(i.assetId)];
        return record(asset) && asset.type === 'image' && asset.remoteUrl === SOURCE_BRAND_INTRO_IMAGE;
      }
      return true;
    });
  } catch { return false; }
}
export function patchSourceBrandIntro(data: SourceBrandIntro, patch: BrandIntroTextPatch): SourceBrandIntro {
  const title = patch.title ?? data.title;
  if (!title.trim()) throw Error('카드 제목을 입력해 주세요.');
  const subtitle = patch.subtitle ?? data.subtitle;
  const next = { ...data, title };
  if (subtitle?.trim()) next.subtitle = subtitle;
  else delete next.subtitle;
  return readSourceBrandIntro(next)!;
}
/** Keep fractional boundaries as provenance, but reproduce original Sequence ceil rounding. */
export function importSourceBrandIntro(input: {
  intro: unknown; firstStartSec: number; nextStartSec?: number; movieEndSec: number;
  sourceFps: number; width: number; height: number; sourceId?: string;
}): { data: SourceBrandIntro; from: number; durationInFrames: number } {
  const { firstStartSec: start, sourceFps: fps } = input;
  const end = input.nextStartSec ?? input.movieEndSec;
  if (!record(input.intro) || !finite(start) || start < 0 || !finite(end) || end <= start || !positive(fps)) throw Error('원본 오프닝 카드 구간을 확인해 주세요.');
  const from = Math.ceil(start * fps), durationInFrames = Math.max(1, Math.ceil(end * fps) - from);
  if (!Number.isSafeInteger(from) || !Number.isSafeInteger(durationInFrames)) throw Error('원본 오프닝 카드 구간을 확인해 주세요.');
  const data = readSourceBrandIntro({ ...input.intro, version: 1, width: input.width, height: input.height,
    sourceFps: fps, clock: { startInSeconds: 0, endInSeconds: durationInFrames / fps },
    provenance: { kind: 'story', originalStartSec: start, originalEndSec: end, ...(input.sourceId ? { sourceId: input.sourceId } : {}) } })!;
  return { data, from, durationInFrames };
}
export function sourceBrandIntroPaintClock(data: SourceBrandIntro, localFrame: number, nativeFps: number) {
  if (!positive(nativeFps) || !finite(localFrame)) throw Error('오프닝 카드 재생 시각을 확인해 주세요.');
  return { frame: (localFrame / nativeFps - data.clock.startInSeconds) * data.sourceFps,
    durationInFrames: (data.clock.endInSeconds - data.clock.startInSeconds) * data.sourceFps };
}
/** Lead must call from shared trim/split clock hook; moves use offset=0. */
export function preserveSourceBrandIntroClock<T extends { sourceBrandIntro?: SourceBrandIntro }>(item: T, fps: number, offsetFrames = 0): T {
  if (!item.sourceBrandIntro) return item;
  if (!positive(fps) || !finite(offsetFrames)) throw Error('오프닝 카드 재생 시각을 확인해 주세요.');
  const data = item.sourceBrandIntro;
  return { ...item, sourceBrandIntro: { ...data, clock: { startInSeconds: data.clock.startInSeconds - offsetFrames / fps,
    endInSeconds: data.clock.endInSeconds - offsetFrames / fps } } };
}

/** Same data-only procedural image contract in source projection and native add. */
export function sourceBrandIntroImageFields(input:{id:string;assetId:string;data:SourceBrandIntro;from:number;durationInFrames:number;width:number;height:number}){
  readSourceBrandIntro(input.data);
  if(![input.from,input.durationInFrames].every(Number.isSafeInteger)||input.from<0||input.durationInFrames<1)throw Error('오프닝 카드 구간을 확인해 주세요.');
  return {id:input.id,type:'image' as const,assetId:input.assetId,from:input.from,durationInFrames:input.durationInFrames,
    left:0,top:0,width:input.width,height:input.height,opacity:1,isDraggingInTimeline:false as const,rotation:0,
    borderRadius:0,cropLeft:0,cropTop:0,cropRight:0,cropBottom:0,keepAspectRatio:false,
    fadeInDurationInSeconds:0,fadeOutDurationInSeconds:0,sourceBrandIntro:input.data};
}
